import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { music } from "@/lib/player";
import { useLang } from "@/context/LangContext";
import { Pause, Play, SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";

const fmtTime = (s) => {
    const v = Math.max(0, Math.round(s || 0));
    return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

const EqBars = ({ playing }) => (
    <span className="flex h-3.5 items-end gap-[2.5px]" aria-hidden="true">
        {[0, 1, 2].map((i) => (
            <motion.span
                key={i}
                className="w-[3px] rounded-full bg-blood"
                animate={
                    playing
                        ? { height: ["4px", "14px", "6px", "12px", "4px"] }
                        : { height: "4px" }
                }
                transition={
                    playing
                        ? { duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }
                        : { duration: 0.3 }
                }
            />
        ))}
    </span>
);

// titre défilant en boucle quand il dépasse la largeur dispo (sinon statique)
const MarqueeText = ({ text, testid, className }) => {
    const boxRef = useRef(null);
    const [overflow, setOverflow] = useState(false);
    useEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const check = () => setOverflow(el.scrollWidth > el.clientWidth + 4);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [text]);
    const secs = Math.max(6, (text || "").length * 0.22);
    return (
        <span ref={boxRef} data-testid={testid} className={`relative block overflow-hidden whitespace-nowrap ${className}`}>
            {overflow ? (
                <motion.span
                    className="inline-flex"
                    animate={{ x: ["0%", "-50%"] }}
                    transition={{ duration: secs, repeat: Infinity, ease: "linear" }}
                >
                    <span className="pr-10">{text}</span>
                    <span className="pr-10">{text}</span>
                </motion.span>
            ) : (
                <span>{text}</span>
            )}
        </span>
    );
};

export default function MusicPlayer() {
    const [state, setState] = useState(music.state);
    const { lang } = useLang();
    const [open, setOpen] = useState(false);
    const [isTouch, setIsTouch] = useState(false);
    const [compact, setCompact] = useState(false);

    useEffect(() => music.subscribe(setState), []);
    // pré-charge le player YouTube pour que le 1er clic lance le son immédiatement :
    // au repos après 1,5 s, ou dès la première interaction (tap/scroll) — au plus tôt
    useEffect(() => {
        const warm = () => music.warmup();
        const t = setTimeout(warm, 1500);
        window.addEventListener("pointerdown", warm, { once: true });
        return () => {
            clearTimeout(t);
            window.removeEventListener("pointerdown", warm);
        };
    }, []);
    useEffect(() => {
        // NE PAS pré-monter le player YT au chargement — il entre en conflit avec
        // l'iframe vidéo visible (deux instances YouTube = "video unavailable").
        // Le player est créé à la demande (audio seul) quand on joue un son.
        return () => {};
    }, []);
    useEffect(() => {
        const mq = window.matchMedia("(hover: none), (max-width: 767px)");
        const update = () => {
            setIsTouch(window.matchMedia("(hover: none)").matches);
            setCompact(mq.matches);
        };
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);
    const playing = state.status === "playing";
    // compact (mobile/touch) : UN SEUL minuteur partagé — 9s, réinitialisé à CHAQUE
    // interaction (play, suivant, volume…) pour laisser le temps de changer de son
    const closeTimer = useRef(null);
    const pokeOpen = () => {
        if (!compact) return;
        setOpen(true);
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), 9000);
    };
    useEffect(() => {
        if (!compact) return undefined;
        if (playing || state.status !== "off") pokeOpen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing, compact, state.status]);
    useEffect(() => () => closeTimer.current && clearTimeout(closeTimer.current), []);
    const expanded = open || (playing && !compact);

    // position du son — mise à jour toutes les 500 ms pendant la lecture
    const [time, setTime] = useState({ t: 0, d: 0 });
    useEffect(() => {
        if (state.status === "off") return undefined;
        const id = setInterval(() => setTime(music.getTime()), 500);
        return () => clearInterval(id);
    }, [state.status, state.title]);

    // tap sur la bulle repliée : ouvre le lecteur (et lance le son si rien n'est chargé)
    const onBubbleTap = () => {
        if (expanded) return;
        if (compact) pokeOpen();
        else setOpen(true);
        if (!playing && state.status === "off") music.toggle();
    };

    // les … rouges : bouton dédié qui ouvre ou rabat le lecteur
    const onDotsTap = (e) => {
        e.stopPropagation();
        if (compact) {
            if (closeTimer.current) clearTimeout(closeTimer.current);
            setOpen((v) => {
                if (v) return false;
                closeTimer.current = setTimeout(() => setOpen(false), 9000);
                return true;
            });
        } else {
            setOpen((v) => !v);
        }
    };

    return (
        <motion.div
            data-testid="music-player"
            data-playing={playing}
            data-muted={state.muted}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 2.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 z-[64] md:bottom-8 md:right-24"
            onMouseEnter={() => !compact && setOpen(true)}
            onMouseLeave={() => !compact && setOpen(false)}
            onClick={onBubbleTap}
        >
            <div className="flex items-center gap-1.5 rounded-full border border-white/15 bg-ink/75 py-1.5 pl-2 pr-1.5 shadow-[0_10px_40px_rgba(0,0,0,0.5)] backdrop-blur-md md:gap-2 md:py-2 md:pl-3 md:pr-2">
                <button
                    data-testid="music-toggle-btn"
                    onClick={onDotsTap}
                    aria-label={expanded ? "Rabattre le lecteur" : "Ouvrir le lecteur"}
                    aria-expanded={expanded}
                    className="flex h-9 w-7 shrink-0 items-center justify-center md:h-8 md:w-8"
                >
                    <EqBars playing={playing} />
                </button>
                {/* play / pause — REC-style red dot, anneau d'invitation quand rien ne joue */}
                <button
                    data-testid="music-play-btn"
                    data-cursor="SON"
                    onClick={(e) => { e.stopPropagation(); music.toggle(); pokeOpen(); }}
                    aria-label={playing ? "Pause" : "Play"}
                    aria-pressed={playing}
                    className={`relative flex h-9 w-9 items-center justify-center rounded-full transition-all duration-300 ${
                        playing
                            ? "bg-blood shadow-[0_0_18px_rgba(225,6,44,0.55)]"
                            : "border border-blood/70 hover:bg-blood/20"
                    }`}
                >
                    {state.status === "off" && (
                        <span className="absolute inset-0 animate-pulse-ring rounded-full border border-blood/70" />
                    )}
                    {playing ? (
                        <Pause size={13} className="fill-cream text-cream" />
                    ) : (
                        <Play size={13} className="ml-0.5 fill-blood text-blood" />
                    )}
                </button>
                {/* invitation à écouter — visible même replié (smartphone) tant que rien ne joue */}
                {state.status === "off" && (
                    <motion.span
                        data-testid="music-invite"
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className="hidden items-center gap-2 whitespace-nowrap pr-1 md:flex"
                    >
                        <motion.span
                            animate={{ opacity: [0.45, 1, 0.45] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            className="font-mono text-[9px] tracking-[0.25em] text-gold"
                        >
                            {lang === "fr" ? "ÉCOUTER GLM RADIO" : "LISTEN TO GLM RADIO"}
                        </motion.span>
                        <motion.span
                            animate={{ x: [0, 4, 0] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                            className="text-gold"
                        >
                            <Play size={9} className="fill-gold" />
                        </motion.span>
                    </motion.span>
                )}
                {/* titre du son — TOUJOURS visible quand un son est chargé, même replié */}
                {state.status !== "off" && !expanded && state.title && (
                    <motion.span
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4 }}
                        className="flex min-w-0 items-center gap-2 pr-1"
                    >
                        {state.cover && (
                            <img
                                data-testid="music-cover-mini"
                                src={state.cover}
                                alt=""
                                className="h-6 w-6 shrink-0 rounded-full border border-white/20 object-cover"
                            />
                        )}
                        <MarqueeText
                            testid="music-track-mini"
                            text={state.title}
                            className="max-w-[64px] font-mono text-[9px] tracking-[0.18em] text-cream/70 md:max-w-[200px]"
                        />
                    </motion.span>
                )}
                {/* track + controls, revealed */}
                <motion.div
                    animate={{ width: expanded ? "auto" : 0, opacity: expanded ? 1 : 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-2 overflow-hidden"
                >
                    <button
                        data-testid="music-prev-btn"
                        onClick={(e) => { e.stopPropagation(); music.prev(); pokeOpen(); }}
                        aria-label="Précédent / début"
                        title="Début / précédent"
                        className="text-cream/50 transition-colors hover:text-cream"
                    >
                        <SkipBack size={13} />
                    </button>
                    {state.cover && (
                        <img
                            data-testid="music-cover"
                            src={state.cover}
                            alt=""
                            className="h-7 w-7 shrink-0 rounded-full border border-white/20 object-cover"
                        />
                    )}
                    <MarqueeText
                        testid="music-track"
                        text={state.title || (lang === "fr" ? "CHOISISSEZ UN SON" : "PICK A TRACK")}
                        className="max-w-[120px] font-mono text-[9px] tracking-[0.2em] text-cream/70 md:max-w-[180px]"
                    />
                    <button
                        data-testid="music-next-btn"
                        onClick={(e) => { e.stopPropagation(); music.next(); pokeOpen(); }}
                        aria-label="Next track"
                        className="text-cream/50 transition-colors hover:text-cream"
                    >
                        <SkipForward size={13} />
                    </button>
                    <button
                        data-testid="music-mute-btn"
                        onClick={(e) => { e.stopPropagation(); music.toggleMute(); pokeOpen(); }}
                        aria-label={state.muted ? "Unmute" : "Mute"}
                        aria-pressed={state.muted}
                        className="text-cream/50 transition-colors hover:text-cream"
                    >
                        {state.muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                    </button>
                    <input
                        data-testid="music-volume-slider"
                        type="range"
                        min="0"
                        max="100"
                        value={Math.round(state.volume * 100)}
                        onChange={(e) => { music.setVolume(Number(e.target.value) / 100); pokeOpen(); }}
                        aria-label="Volume"
                        className="hidden h-1 w-14 cursor-pointer accent-blood md:block"
                    />
                </motion.div>
            </div>
            {/* curseur de progression — avancer/reculer dans le son */}
            <motion.div
                animate={{ height: expanded ? "auto" : 0, opacity: expanded ? 1 : 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
            >
                <div className="flex items-center gap-2 pt-2">
                    <span data-testid="music-time-current" className="font-mono text-[8px] tabular-nums tracking-[0.1em] text-cream/50">{fmtTime(time.t)}</span>
                    <input
                        data-testid="music-seek-slider"
                        type="range"
                        min="0"
                        max={Math.max(Math.round(time.d), 1)}
                        value={Math.round(Math.min(time.t, time.d || 0))}
                        onChange={(e) => { music.seekTo(Number(e.target.value)); pokeOpen(); }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Position dans le son"
                        className="h-1 w-full min-w-[120px] cursor-pointer accent-blood"
                    />
                    <span data-testid="music-time-total" className="font-mono text-[8px] tabular-nums tracking-[0.1em] text-cream/50">{fmtTime(time.d)}</span>
                </div>
            </motion.div>
        </motion.div>
    );
}
